/**
 * Zod schemas for Category Variants pipeline events (A4+, A5).
 *
 * wallpaper.categorize.completed   — emitted by ai-categorize after writing ranked list
 * wallpaper.top_n.applied          — emitted by top-n-applier after diff/apply
 * settings.updated                 — emitted by update_app_setting RPC (DB-side)
 * wallpaper.variant.invalidated    — ONE event per wallpaper, carrying the locale SET
 *                                    (emitted by ai-seo-orchestrator or prompt-invalidator)
 * wallpaper.seo.variant_ready      — emitted by ai-seo-variant PER written locale
 */

import { z } from 'zod';

/** wallpaper.categorize.completed */
export const wallpaperCategorizeCompletedSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  // ai_step_id is optional — emitted by ai-categorize handler (A5)
  // TODO(A5): make ai_step_id required once ai-categorize emits it
  ai_step_id: z.number().int().positive().optional(),
});

/** wallpaper.top_n.applied */
export const wallpaperTopNAppliedSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  added: z.array(z.number().int().positive()),
  removed: z.array(z.number().int().positive()),
});

/** settings.updated (emitted by update_app_setting DB RPC) */
export const settingsUpdatedSchema = z.object({
  namespace: z.string(),
  key: z.string(),
  old: z.unknown().optional(),
  new: z.unknown().optional(),
});

/**
 * wallpaper.variant.invalidated
 *
 * Emitted by ai-seo-orchestrator as ONE event carrying the full locale SET.
 * Also emitted by prompt-invalidator when a seo_variant prompt is updated
 * (one event per wallpaper carrying all affected locales).
 * The handler makes ONE multi-lang LLM call and upserts N wallpapers_i18n rows.
 */
export const wallpaperVariantInvalidatedSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  /** Non-empty set of locale codes to (re)generate in one LLM call. */
  lang_codes: z.array(z.string().min(2)).min(1),
});

/**
 * wallpaper.seo.variant_ready
 *
 * Emitted by ai-seo-variant after successfully upserting a wallpapers_i18n
 * row for one (wallpaper, locale) pair.
 */
export const wallpaperSeoVariantReadySchema = z.object({
  wallpaper_id: z.number().int().positive(),
  lang_code: z.string().min(2),
  ai_step_id: z.number().int().positive(),
});
